import { lazy } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from "react-router";
import { featureFlags, isFeatureEnabled } from "src/utils/featureFlag";

// Eager: app shell, error boundary, and the landing/catch-all routes. These are
// small and always needed for first paint, so they stay in the entry chunk.
import AppLayout from "src/components/AppLayout/AppLayout";
import ErrorFallbackDialog from "src/components/ErrorBoundary/ErrorFallbackDialog";
import EmptyLandingPage from "src/components/EmptyLandingPage/EmptyLandingPage";
import CatchAll from "src/components/CatchAll";
import ShipperRedirect from "src/components/ShipperMaint/ShipperRedirect";

// Hidden DM surface — mounted at /dm/*, sibling of the main RP route tree
// so DM keeps its own AppLayout chrome instead of being wrapped in RP's.
// Not linked from RP's nav; reachable by direct URL only.
import { dmRoutes } from "src/dm/utils/routesConfig";

// Lazy: every feature screen becomes its own chunk, fetched on first navigation
// to its route instead of being bundled into the entry chunk. Keeps the initial
// download small. The <Suspense> boundary lives around <Outlet /> in AppLayout.
const BatchItemDetails = lazy(
  () => import("src/components/BatchItemDetails/BatchItemDetails"),
);
const Dashboard = lazy(() => import("src/components/Dashboard/Dashboard"));
const NewBatchUpload = lazy(
  () => import("src/components/NewBatchUpload/NewBatchUpload"),
);
const Stores = lazy(() => import("src/components/Stores/Stores"));
const StoresDetails = lazy(() => import("src/components/StoresDetails"));
const ZoneBanners = lazy(() => import("src/components/ZoneBanners/ZoneBanners"));
const Suppliers = lazy(() => import("src/components/Suppliers/Suppliers"));
const CreateStore = lazy(() => import("src/components/CreateStore/CreateStore"));
const CreateSupplier = lazy(
  () => import("src/components/CreateSupplier/CreateSupplier"),
);
const SupplierDetails = lazy(
  () => import("src/components/Suppliers/SupplierDetails/SupplierDetails"),
);
const ReceivingRestrictions = lazy(
  () => import("src/components/ReceivingRestrictions/ReceivingRestrictions"),
);
const ItemMaintenance = lazy(
  () => import("src/components/ItemMaintenance/ItemMaintenance"),
);
const ItemDetail = lazy(() => import("src/components/ItemMaintenance/ItemDetail"));
const NewItemWizard = lazy(
  () => import("src/components/ItemMaintenance/NewItemWizard"),
);
const NewSupplierItemWizard = lazy(
  () => import("src/components/ItemMaintenance/NewSupplierItemWizard"),
);
const ItemStateFlag = lazy(() => import("src/components/SNAP/ItemStateFlag"));
const PseudoUpc = lazy(() => import("src/components/PseudoUpc/PseudoUpc"));
const PriceGroupMove = lazy(() => import("src/components/PriceGroupMove/PriceGroupMove"));
const StateSubgroups = lazy(() => import("src/components/SNAP/StateSubgroups"));
const MaintainStoreCoupons = lazy(
  () => import("src/components/MaintainStoreCoupons/MaintainStoreCoupons"),
);
const MiscellaneousTransactionCodes = lazy(
  () =>
    import(
      "src/components/MiscellaneousTransactionCodes/MiscellaneousTransactionCodes"
    ),
);
const TagHandlingInstructions = lazy(
  () => import("src/components/TagHandlingInstructions/TagHandlingInstructions"),
);
const ReplacementStoreRequest = lazy(
  () => import("src/components/TagRequests/ReplacementStoreRequest"),
);
const FullScanRequest = lazy(
  () => import("src/components/TagRequests/FullScanRequest"),
);
const QaAccess = lazy(() => import("src/components/Admin/QaAccess"));
const DisplayPriceDetails = lazy(
  () => import("src/components/Pricing/DisplayPriceDetails"),
);
const FamilyMaintSearch = lazy(
  () => import("src/components/Pricing/FamilyMaint/FamilyMaintSearch"),
);
const FamilyMaintDetail = lazy(
  () => import("src/components/Pricing/FamilyMaint/FamilyMaintDetail"),
);
const ShipperCost = lazy(() => import("src/components/ShipperCost/ShipperCost"));
const DealCost = lazy(() => import("src/components/DealCost/DealCost"));
const DisplayCostDetails = lazy(
  () => import("src/components/DisplayCostDetails/DisplayCostDetails"),
);
const DSDCostLink = lazy(() => import("src/components/DSDCostLink/DSDCostLink"));
const Promotions = lazy(() => import("src/components/Promotions/Promotions"));
const PromotionDetail = lazy(
  () => import("src/components/Promotions/PromotionDetail/PromotionDetail"),
);
const CopyStoreDSD = lazy(
  () => import("src/components/CopyStoreDSD/CopyStoreDSD"),
);
const StoreSuppliers = lazy(
  () => import("src/components/StoreSuppliers/StoreSuppliers"),
);
const StoreZones = lazy(() => import("src/components/StoreZones/StoreZones"));
const CopyStoreZones = lazy(
  () => import("src/components/CopyStoreZones/CopyStoreZones"),
);
const StoreDeptXrefs = lazy(
  () => import("src/components/StoreDeptXrefs/StoreDeptXrefs"),
);
const ScaleDepartments = lazy(
  () => import("src/components/ScaleDepartments/ScaleDepartments"),
);
const ScaleTareCodes = lazy(
  () => import("src/components/ScaleTareCodes/ScaleTareCodes"),
);
const CaseInterpackItem = lazy(
  () => import("src/components/CaseInterpackItem/CaseInterpackItem"),
);
const PosFileCompare = lazy(
  () => import("src/components/PosFileCompare/PosFileCompare"),
);
const Journal = lazy(() => import("src/components/Journal/Journal"));
const ValidationList = lazy(
  () => import("src/components/ValidationList/ValidationList"),
);
const UpcRecalls = lazy(() => import("src/components/UpcRecalls/UpcRecalls"));
const AdGroups = lazy(() => import("src/components/AdGroups/AdGroups"));
const AdBlocks = lazy(() => import("src/components/AdBlocks/AdBlocks"));
const AdBlockDetailPage = lazy(
  () => import("src/components/AdBlocks/AdBlockDetailPage"),
);
const Scanbacks = lazy(() => import("src/components/Scanbacks/Scanbacks"));
const EOffers = lazy(() => import("src/components/EOffers/EOffers"));
const SecondaryPromotions = lazy(
  () => import("src/components/SecondaryPromotions/SecondaryPromotions"),
);
const SpecialtyPromotions = lazy(
  () => import("src/components/SpecialtyPromotions/SpecialtyPromotions"),
);
const UserProfile = lazy(() => import("src/components/UserProfile/UserProfile"));
const PromotionWizard = lazy(
  () => import("src/components/Promotions/PromotionWizard/PromotionWizard"),
);
const MaintainPromotionPrices = lazy(
  () => import("src/components/MaintainPromotionPrices/MaintainPromotionPrices"),
);
const AddFuturePromotions = lazy(
  () => import("src/components/AddFuturePromotions/AddFuturePromotions"),
);
const PromoPricingByBanner = lazy(
  () => import("src/components/PromoPricingByBanner/PromoPricingByBanner"),
);
const RegularPricing = lazy(
  () => import("src/components/RegularPricing/RegularPricing"),
);
const PromoPricing = lazy(
  () => import("src/components/PromoPricing/PromoPricing"),
);
const AddPendingPricing = lazy(
  () => import("src/components/AddPendingPricing/AddPendingPricing"),
);

export const routesConfig = (userName: string) =>
  createBrowserRouter(
    createRoutesFromElements(
      <>
        {dmRoutes}
        <Route
          path="/"
          element={<AppLayout userName={userName} />}
          errorElement={<ErrorFallbackDialog />}
        >
        <Route path="/" element={<EmptyLandingPage />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/batch-upload/status" element={<Dashboard />} />
        <Route path="/batch-upload/create" element={<NewBatchUpload />} />
        <Route
          path="/batch-upload/:batchNumber/:batchType"
          element={<BatchItemDetails />}
        />
        {isFeatureEnabled(featureFlags.Stores) && (
          <>
            <Route path="/stores" element={<Stores />} />
            <Route path="/stores/create" element={<CreateStore />} />
            <Route path="/stores/:storeCode" element={<StoresDetails />} />
          </>
        )}
        {isFeatureEnabled(featureFlags.CopyStoreDSD) && (
          <Route path="/stores/copy-store-dsd" element={<CopyStoreDSD />} />
        )}
        {isFeatureEnabled(featureFlags.CopyStoreZones) && (
          <Route path="/stores/copy-store-zones" element={<CopyStoreZones />} />
        )}
        {isFeatureEnabled(featureFlags.ZoneBanners) && (
          <Route path="/stores/zone-banners" element={<ZoneBanners />} />
        )}
        {isFeatureEnabled(featureFlags.Suppliers) && (
          <>
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/suppliers/create" element={<CreateSupplier />} />
            <Route
              path="/suppliers/:supplierNumber"
              element={<SupplierDetails />}
            />
          </>
        )}
        {isFeatureEnabled(featureFlags.ItemAuthorizations) && (
          <Route path="/items/item-auth" element={<ReceivingRestrictions />} />
        )}
        {isFeatureEnabled(featureFlags.ItemMaintenance) && (
          <>
            <Route path="/items/maintenance" element={<ItemMaintenance />} />
            <Route path="/items/maintenance/new" element={<NewItemWizard />} />
            <Route
              path="/items/maintenance/:ritemNumber"
              element={<ItemDetail />}
            />
            <Route
              path="/items/maintenance/:ritemNumber/new-supplier-item"
              element={<NewSupplierItemWizard />}
            />
          </>
        )}
        {isFeatureEnabled(featureFlags.ItemStateFlag) && (
          <Route path="/items/item-state-flag" element={<ItemStateFlag />} />
        )}
        {isFeatureEnabled(featureFlags.PseudoUpc) && (
          <Route path="/items/pseudo-upcs" element={<PseudoUpc />} />
        )}
        {isFeatureEnabled(featureFlags.PriceGroupMove) && (
          <Route path="/pricing/price-group-moves" element={<PriceGroupMove />} />
        )}
        {isFeatureEnabled(featureFlags.MaintainStoreCoupons) && (
          <Route
            path="/items/maintain-store-coupons"
            element={<MaintainStoreCoupons />}
          />
        )}
        {isFeatureEnabled(featureFlags.MiscellaneousTransactionCodes) && (
          <Route
            path="/items/misc-transaction-codes"
            element={<MiscellaneousTransactionCodes />}
          />
        )}
        {isFeatureEnabled(featureFlags.TagHandlingInstructions) && (
          <Route
            path="/items/tag-handling-instructions"
            element={<TagHandlingInstructions />}
          />
        )}
        {isFeatureEnabled(featureFlags.EOffers) && (
          <Route path="/promotions/eoffers" element={<EOffers />} />
        )}
        {isFeatureEnabled(featureFlags.TagReplacementStoreRequest) && (
          <Route
            path="/items/tag-requests/replacement-store"
            element={<ReplacementStoreRequest />}
          />
        )}
        {isFeatureEnabled(featureFlags.TagFullScanShelfTagRequest) && (
          <Route
            path="/items/tag-requests/full-scan"
            element={<FullScanRequest />}
          />
        )}
        {isFeatureEnabled(featureFlags.DisplayPriceDetails) && (
          <Route
            path="/pricing/display-price-details"
            element={<DisplayPriceDetails />}
          />
        )}
        {/* The menu has pointed at /admin/security since SecurityManagement
            was added, with no route behind it. QA Access fills it. The flag is
            off in production and the API refuses the writes there regardless. */}
        {isFeatureEnabled(featureFlags.SecurityManagement) && (
          <Route path="/admin/security" element={<QaAccess />} />
        )}
        {isFeatureEnabled(featureFlags.FamilyMaint) && (
          <>
            <Route
              path="/pricing/family-maint"
              element={<FamilyMaintSearch />}
            />
            <Route
              path="/pricing/family-maint/family/:familyCode"
              element={<FamilyMaintDetail view="byFamily" />}
            />
            <Route
              path="/pricing/family-maint/item/:retailItemNumber"
              element={<FamilyMaintDetail view="byItem" />}
            />
          </>
        )}
        {isFeatureEnabled(featureFlags.ScaleDepartments) && (
          <Route
            path="/items/scale-departments"
            element={<ScaleDepartments />}
          />
        )}
        {isFeatureEnabled(featureFlags.ScaleTareCodes) && (
          <Route path="/items/scale-tare-codes" element={<ScaleTareCodes />} />
        )}
        {isFeatureEnabled(featureFlags.CaseInterpackItem) && (
          <Route path="/items/case-interpak" element={<CaseInterpackItem />} />
        )}
        {isFeatureEnabled(featureFlags.PosFileCompare) && (
          <Route path="/stores/pos-file-compare" element={<PosFileCompare />} />
        )}
        {isFeatureEnabled(featureFlags.Journal) && (
          <Route path="/admin/journal" element={<Journal />} />
        )}
        {(isFeatureEnabled(featureFlags.PromotionTypesRules) ||
          isFeatureEnabled(featureFlags.PromotionBenchmarkCodes) ||
          isFeatureEnabled(featureFlags.PromotionItemTypes) ||
          isFeatureEnabled(featureFlags.PromotionGroups) ||
          isFeatureEnabled(featureFlags.RewardPromotionStatus) ||
          isFeatureEnabled(featureFlags.AdDisplayPlan) ||
          isFeatureEnabled(featureFlags.ScanbackEntryTypes) ||
          isFeatureEnabled(featureFlags.AdType) ||
          isFeatureEnabled(featureFlags.SecondaryPromoLinks) ||
          isFeatureEnabled(featureFlags.ScanbackBusinessUnits) ||
          isFeatureEnabled(featureFlags.AdvertisedItemCodes) ||
          isFeatureEnabled(featureFlags.PromotionActionCodes) ||
          isFeatureEnabled(featureFlags.AdPriceFormat) ||
          isFeatureEnabled(featureFlags.DistributionMethod) ||
          isFeatureEnabled(featureFlags.AdLocation) ||
          isFeatureEnabled(featureFlags.RuleOperationCodes) ||
          isFeatureEnabled(featureFlags.AccountPromotionXref) ||
          isFeatureEnabled(featureFlags.RuleBaseCode) ||
          isFeatureEnabled(featureFlags.PriceBookCodes) ||
          isFeatureEnabled(featureFlags.PriceBasis) ||
          isFeatureEnabled(featureFlags.EdlpDeleteDate) ||
          isFeatureEnabled(featureFlags.PriceReasonCodes) ||
          isFeatureEnabled(featureFlags.PriceStatusCodes) ||
          isFeatureEnabled(featureFlags.CompetitorBanners) ||
          isFeatureEnabled(featureFlags.RuleTargetCodes)) && (
          <Route
            path="/admin/validation-list"
            element={<ValidationList />}
          />
        )}
        {isFeatureEnabled(featureFlags.UpcRecalls) && (
          <Route path="/items/upc-recalls" element={<UpcRecalls />} />
        )}
        {isFeatureEnabled(featureFlags.AdGroups) && (
          <Route path="/promotions/ad-groups" element={<AdGroups />} />
        )}
        {isFeatureEnabled(featureFlags.AdBlocks) && (
          <>
            <Route path="/promotions/ad-blocks" element={<AdBlocks />} />
            {/* This path has no `:id`, so the page cannot infer create mode
                from the route parameter — it is told outright. */}
            <Route
              path="/promotions/ad-blocks/new"
              element={<AdBlockDetailPage mode="new" />}
            />
            <Route
              path="/promotions/ad-blocks/:id"
              element={<AdBlockDetailPage />}
            />
          </>
        )}
        {isFeatureEnabled(featureFlags.Scanbacks) && (
          <Route path="/promotions/scanbacks" element={<Scanbacks />} />
        )}
        {isFeatureEnabled(featureFlags.SecondaryPromotions) && (
          <Route
            path="/promotions/secondary"
            element={<SecondaryPromotions />}
          />
        )}
        {isFeatureEnabled(featureFlags.SpecialtyPromotions) && (
          <Route
            path="/promotions/specialty"
            element={<SpecialtyPromotions />}
          />
        )}
        {/* Shipper Maint, Display Shippers and Shipper Retail live on Item
            Maintenance now (2026-10-20); their old addresses resolve there. */}
        <Route path="/items/shipper-maint" element={<ShipperRedirect />} />
        <Route path="/items/display-shippers" element={<ShipperRedirect />} />
        <Route path="/pricing/shipper-retail" element={<ShipperRedirect />} />
        {isFeatureEnabled(featureFlags.MaintainPromotionPrices) && (
          <Route
            path="/promotions/maintain-prices"
            element={<MaintainPromotionPrices />}
          />
        )}
        {isFeatureEnabled(featureFlags.AddFuturePromotions) && (
          <Route
            path="/promotions/add-future"
            element={<AddFuturePromotions />}
          />
        )}
        {isFeatureEnabled(featureFlags.PromoPricingByBanner) && (
          <Route
            path="/promotions/pricing-by-banner"
            element={<PromoPricingByBanner />}
          />
        )}
        {isFeatureEnabled(featureFlags.RegularPricing) && (
          <Route
            path="/pricing/regular-by-banner"
            element={<RegularPricing />}
          />
        )}
        {isFeatureEnabled(featureFlags.PromoPricing) && (
          <Route
            path="/pricing/promo-by-banner"
            element={<PromoPricing />}
          />
        )}
        {isFeatureEnabled(featureFlags.AddPendingPricing) && (
          <Route path="/pricing/add-future" element={<AddPendingPricing />} />
        )}
        {isFeatureEnabled(featureFlags.Admin) && (
          <Route path="/admin/state-subgroups" element={<StateSubgroups />} />
        )}
        {isFeatureEnabled(featureFlags.ShipperCost) && (
          <Route path="/cost/shipper-cost" element={<ShipperCost />} />
        )}
        {isFeatureEnabled(featureFlags.DealCost) && (
          <Route path="/cost/deal-cost" element={<DealCost />} />
        )}
        {isFeatureEnabled(featureFlags.DisplayCostDetails) && (
          <Route
            path="/cost/display-cost-details"
            element={<DisplayCostDetails />}
          />
        )}
        {isFeatureEnabled(featureFlags.DSDCostLink) && (
          <Route path="/cost/dsd-cost-link" element={<DSDCostLink />} />
        )}
        {isFeatureEnabled(featureFlags.StoreSuppliers) && (
          <Route path="/stores/store-suppliers" element={<StoreSuppliers />} />
        )}
        {isFeatureEnabled(featureFlags.StoreZones) && (
          <Route path="/stores/store-zones" element={<StoreZones />} />
        )}
        {isFeatureEnabled(featureFlags.StoreDeptXrefs) && (
          <Route path="/stores/store-dept-xrefs" element={<StoreDeptXrefs />} />
        )}
        {isFeatureEnabled(featureFlags.Promotions) && (
          <>
            <Route path="/promotions" element={<Promotions />} />
            <Route
              path="/promotions/create"
              element={<PromotionWizard mode="create" />}
            />
            <Route
              path="/promotions/:promotionId/edit"
              element={<PromotionWizard mode="edit" />}
            />
            <Route
              path="/promotions/:promotionId"
              element={<PromotionDetail />}
            />
          </>
        )}
        <Route path="*" element={<CatchAll />} />
        </Route>
      </>
    )
  );
